/** @jsx jsx */
import { jsx } from 'theme-ui';
import { Fragment, useCallback } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/core';
import isNil from 'lodash/isNil';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { IoMdVideocam } from 'react-icons/io';
import { useHistory } from 'react-router-dom';
import { AppointmentStatus } from '../../../firebase/firestore/documents/appointmentStatus';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import {
  AppointmentType,
  AvailableBookingWithPosition,
  Booking,
} from '../../../firebase/firestore/documents/appointmentAvaiability';
import { RemoteConfigKey } from '../../../firebase/remoteConfig';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { INTERVIEW_MESSAGING_TABS, MESSAGING_CANDIDATE_TABS } from '../../../routes/constants';
import colors from '../../../styles/colors';
import { SeekerContainerTabItems } from '../../messaging/seeker/SeekerContainerContext';
import { SESSION_KEY_CANDIDATE_LIST_TYPE } from '../../messaging/sidebar/filters/SeekersFiltersContext';
import { CandidateListType } from '../../messaging/sidebar/filters/seekersFiltersContextValueType';
import { BookingDetailViewCancelModal } from './BookingDetailViewCancelModal';
import { bookingDrawer } from './BookingDrawer.Style';
import { BookingDetailContent } from './BookingDetailContent';

export type ScheduleByDateProps = {
  isOpen: boolean;
  onClose: () => void;
  bookingDetail: AvailableBookingWithPosition;
};

export const BookingDetailView = ({ isOpen, onClose, bookingDetail }: ScheduleByDateProps): JSX.Element => {
  const { currentUserProfile } = useCurrentUserProfile();
  const { t } = useTranslation('calendar');
  const history = useHistory();

  const setInterviewEvent = useStoreActions((state) => state.interview.setInterviewEvent);
  const { updateRatingFor } = useStoreActions(/* istanbul ignore next */ (actions) => actions.interview);

  const {
    isOpen: isBookingDetailViewModalOpen,
    onOpen: onBookingDetailViewModalOpen,
    onClose: onBookingDetailViewModalClose,
  } = useDisclosure();

  const gracePeriod: number = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.JOIN_VIDEO_GRACE_PERIOD_IN_MINUTE];
  });

  const enableMeetFeature: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_MEET_FEATURE];
  });

  const storeEventDetailForInterview = () => {
    sessionStorage.setItem(SESSION_KEY_CANDIDATE_LIST_TYPE, CandidateListType.UNKNOWN);
    setInterviewEvent(bookingDetail);
    history.push(INTERVIEW_MESSAGING_TABS(bookingDetail.candidate.id, SeekerContainerTabItems.Messaging), {
      redirectedFromPage: 'calendar',
    });
  };

  const storeEventDetailForInterviewRating = () => {
    updateRatingFor({
      name: bookingDetail.candidate.firstName,
      seekerId: bookingDetail.booking.seekerId,
      positionId: bookingDetail.booking.positionId,
      selectedCandidate: bookingDetail.candidate,
    });
    history.push(MESSAGING_CANDIDATE_TABS(bookingDetail.candidate.id, SeekerContainerTabItems.Messaging), {
      redirectedFromPage: 'calendar',
    });
  };

  const isAbleToJoinVideoInterview = useCallback(
    (booking: Booking) => {
      return moment().isBetween(
        moment(booking.date).subtract(5, 'minutes'),
        moment(booking.date).add(booking.duration, 'minutes').add(gracePeriod, 'minute'),
      );
    },
    [gracePeriod],
  );

  const isInPast = useCallback(
    (booking: Booking) => {
      return moment(booking.date).add(booking.duration, 'minutes').add(gracePeriod, 'minute').isBefore(moment());
    },
    [gracePeriod],
  );

  const is24HourLimitExceeded = useCallback((booking: Booking) => {
    return moment(booking.date).add(booking.duration, 'minutes').add(1440, 'minute').isBefore(moment());
  }, []);

  const isInterviewTimeFinished = useCallback((booking: Booking) => {
    return moment(booking.date).add(booking.duration, 'minutes').isBefore(moment());
  }, []);

  const isCurrentTimeBeforeCallStart = useCallback((booking: Booking) => {
    return moment().isBefore(moment(booking.date));
  }, []);

  const allowSuperUsersToDeleteAvailability: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ALLOW_SUPER_USERS_TO_DELETE_AVAILABILITY];
  });

  const appUserId = useStoreState((s) => s.app.user?.id);
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);
  const isAdministrationAccount = !isNil(appOwnersId) && !isNil(appUserId) && appOwnersId?.includes(appUserId);

  // istanbul ignore next
  const showCancelInterviewBtn = () => {
    if (!isInPast(bookingDetail.booking) && isCurrentTimeBeforeCallStart(bookingDetail.booking)) {
      if (allowSuperUsersToDeleteAvailability && isAdministrationAccount) {
        return true;
      }
      if (currentUserProfile?.id === bookingDetail.booking.recruiterId) {
        return true;
      }
      return false;
    }
    return false;
  };

  return (
    <Fragment>
      <BookingDetailViewCancelModal
        bookingDetailViewClose={onClose}
        bookingDetail={bookingDetail}
        isOpen={isBookingDetailViewModalOpen}
        onClose={onBookingDetailViewModalClose}
        showWarning={isAdministrationAccount && currentUserProfile?.id !== bookingDetail.booking.recruiterId}
      />
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay>
          <DrawerContent css={bookingDrawer} className="booking-detail-drawer">
            <DrawerCloseButton _focus={{ border: '1px solid #1F3CBA' }} />
            <DrawerHeader borderColor="#EBEDF3" borderBottomWidth="1px">
              <Heading as="h3" size="sm" color={`${colors.blue.default} !important`}>
                {t('booking.interviewHeader')}
                {!bookingDetail.candidate.dismissed &&
                  bookingDetail.booking.shortCode !== AppointmentStatus.requested &&
                  (bookingDetail.booking.type === AppointmentType.video ||
                    bookingDetail.booking.type[0] === AppointmentType.video) &&
                  enableMeetFeature && (
                    <Button
                      data-testid="VideoCallButton"
                      leftIcon={<IoMdVideocam />}
                      colorScheme="orange"
                      variant="solid"
                      isDisabled={!isAbleToJoinVideoInterview(bookingDetail.booking)}
                      onClick={() => storeEventDetailForInterview()}
                    >
                      {t('eventForm.startVideoInterview')}
                    </Button>
                  )}
              </Heading>
            </DrawerHeader>
            <DrawerBody p="0">
              <BookingDetailContent bookingDetail={bookingDetail} />
            </DrawerBody>
            <Flex justify="flex-start" py={3} borderTop="2px" borderColor="#EBEDF3">
              {/* <Button data-testid="RescheduleEventBtn" colorScheme="blue" variant="solid" borderRadius={3} ml={6}>
                {t('eventForm.rescheduleEvent')}
              </Button> */}
              {showCancelInterviewBtn() && (
                <Button
                  variant="outline"
                  ml={6}
                  borderRadius={3}
                  mr={3}
                  onClick={onBookingDetailViewModalOpen}
                  data-testid="CancelDrawerBtn"
                >
                  {t('booking.cancelEvent')}
                </Button>
              )}

              {isInterviewTimeFinished(bookingDetail.booking) &&
                bookingDetail.candidate &&
                currentUserProfile?.id &&
                bookingDetail.booking.joinedRecruiterId?.includes(currentUserProfile?.id) &&
                !bookingDetail.candidate?.dismissed && (
                  <Tooltip
                    label={is24HourLimitExceeded(bookingDetail.booking) ? t('meet:ratingDisabledTooltip') : ''}
                    fontSize="md"
                    placement="top"
                  >
                    <Box>
                      <Button
                        variant="solid"
                        colorScheme="blue"
                        ml={6}
                        borderRadius={3}
                        data-testid="RateInterviewBtn"
                        mr={3}
                        isDisabled={is24HourLimitExceeded(bookingDetail.booking)}
                        onClick={storeEventDetailForInterviewRating}
                      >
                        {t('interview.rateBtn')}
                      </Button>
                    </Box>
                  </Tooltip>
                )}
            </Flex>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </Fragment>
  );
};
